/**
 * @file Allow Individual Album Join.
 * Determines whether an album should be joined through the Gallery or the flow
 * should be album-based.
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import { graphql } from 'react-apollo';
import compose from 'lodash.flowright';
import get from 'lodash.get';

import { AlbumDataWithParentAndSubscription } from '../../graphql/queries/albums';
import { ALBUM_FOLDER_JOIN_TYPE } from '../../constants/album-folder';
import { hasFolderSubscription } from '../../util/album';

/**
 * Higher order component for the Allow individual album join.
 *
 * @param {React.Component} WrappedComponent The component to be composed.
 * @returns {React.Component} The composed component.
 */
const WithAllowIndividualAlbumJoinHOC = WrappedComponent =>
  class extends Component {
    static propTypes = {
      album: PropTypes.shape({
        id: PropTypes.string,
        subscription_album_folder: PropTypes.shape({
          id: PropTypes.string,
          join_type: PropTypes.oneOf(Object.keys(ALBUM_FOLDER_JOIN_TYPE)),
        }),
      }),
      loading: PropTypes.bool,
    };

    render() {
      const { album, loading } = this.props;

      if (loading) {
        return null;
      }

      return hasFolderSubscription(album) &&
        get(album, 'subscription_album_folder.join_type') !==
          ALBUM_FOLDER_JOIN_TYPE.INDIVIDUAL ? (
        <Redirect
          to={`/discover/album-folder/${get(
            album,
            'subscription_album_folder.id',
          )}/faces`}
        />
      ) : (
        <WrappedComponent {...this.props} />
      );
    }
  };

/**
 * Higher order container for the Allow individual album join functionality.
 *
 * @param {React.Component} WrappedComponent The component to be composed.
 * @returns {Function} The HOC container.
 */
const AllowIndividualAlbumJoin = WrappedComponent => {
  const WithPpdMode = WithAllowIndividualAlbumJoinHOC(WrappedComponent);

  const withAlbumData = graphql(AlbumDataWithParentAndSubscription, {
    options: ({ match }) => ({
      variables: {
        id: match.params.albumId,
      },
    }),
    props: ({ data: { album, loading } }) => ({
      album,
      loading,
    }),
  });

  return compose(withAlbumData)(WithPpdMode);
};

export { WithAllowIndividualAlbumJoinHOC };
export default AllowIndividualAlbumJoin;
